import React, {
  useCallback, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HUMIDITY } from 'components/hooks/graph';
import { getYminMax } from '../../helper/graphHelper';
import NewSensorDataChart from './NewSensorDataChart';
import { VAR_HUMIDITY } from './constants';

interface Sensor {
  name?: string;
  id?: number;
  type?: string;
  identifier?: string;
}

interface HumidityChartProps {
  sensor?: Sensor | any;
  startDate?: any;
  endDate?: any;
  cached?: string | any;
  updateYminmax?: () => void;
  uniformYAxis?: boolean;
  yMinMaxMap?: any;
  header?: boolean;
}

const HumidityChart: React.FC<HumidityChartProps> = (props: any) => {
  const {
    sensor, startDate, endDate, cached, updateYminmax, uniformYAxis, yMinMaxMap,
    header
  } = props;
  const { t } = useTranslation();
  const [label, setLabel] = useState('');

  const globalYMinMax = yMinMaxMap && yMinMaxMap[HUMIDITY]
    ?yMinMaxMap[HUMIDITY]:{min: null, max: null};

  const yMinMax = useMemo(() => {
    if((globalYMinMax.min || globalYMinMax.min===0)
        && globalYMinMax.max) { return { min: globalYMinMax.min, max: globalYMinMax.max}; }
    return null;
  }, [globalYMinMax.min, globalYMinMax.max]);

  const customConfig = useCallback((configObj, seriesData) => {
    if (configObj?.series?.[0]) {
      const yMinMax = getYminMax(seriesData) || { min: 0, max: 0};

      const { multiplier } = configObj?.series?.[0] || { multiplier: 1 };
      updateYminmax(HUMIDITY, yMinMax.min * multiplier, yMinMax.max * multiplier);
      const { units } = seriesData?.temperature?.[0] || {};
      const dataUnit = units || '%RH';
      const data = configObj?.series?.[0].data;
      const yMin = yMinMax?.min;
      const yMax = yMinMax?.max;
      setLabel(dataUnit);
      return {
        ...configObj,
        series: [{
          ...configObj?.series?.[0],
          data,
          tooltip: {
            valueDecimals: 2,
            valueSuffix: ` ${dataUnit}`,
          },
          name: 'Humidity',
        }],
        yAxis: [{
          ...(configObj.yAxis[0] || {
            title: null,
            opposite: false,
            labels: {
              y: -1,
              x: 0,
              align: 'left'
            }
          }),
          min: yMin,
          max: yMax
        }],
      };
    }

    return configObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateYminmax]);

  return (
    <>
      <NewSensorDataChart
        sensorName={sensor.name}
        sensorId={sensor.id}
        sensorType={sensor.type}
        sensorIdentifier={sensor.identifier}
        variables={VAR_HUMIDITY}
        startDate={startDate}
        endDate={endDate}
        cached={cached}
        customConfig={customConfig}
        header={header && {title: t('Humidity'), units: t(label), right: []}}
        uniformYAxis={uniformYAxis}
        yMinMax={yMinMax}
      />
    </>
  );
};

HumidityChart.defaultProps = {
  sensor: null,
  startDate: null,
  endDate: null,
  cached: null,
  updateYminmax: () => null,
  uniformYAxis: false,
  yMinMaxMap: null,
  header: true,
};

export default HumidityChart;
